import {readOsiBreakTimeMinutes} from '../vosDurationUtils'
import type {ExtensionRequestListItem} from '../extensionRequestUtils'
import {formatExtensionEstimatedTime, formatExtensionSubmittedAt} from '../extensionRequestUtils'
import type {BuildDigitalVosFormInput, DigitalVosExtensionBlock, DigitalVosFormViewModel} from './digitalVosFormTypes'
import {
  formatAppointmentDate,
  formatAppointmentDateTime,
  formatAppointmentTime,
  formatScheduledDuration,
  formatTravelTime,
  joinPhoneLines,
} from './formatAppointmentDateTime'

function readString(obj: Record<string, unknown> | null | undefined, ...keys: string[]): string {
  if (!obj) return ''
  for (const key of keys) {
    const v = obj[key]
    if (v != null && v !== '') return String(v).trim()
  }
  return ''
}

function readNotesText(osiNotes: Array<Record<string, unknown>>): string {
  if (!osiNotes?.length) return ''
  const lines = osiNotes
    .map((n) => readString(n, 'note', 'Note'))
    .filter(Boolean)
  return lines.join('\n\n')
}

function mapExtensions(extensions: ExtensionRequestListItem[]): DigitalVosExtensionBlock[] {
  return extensions.map((ext) => ({
    time: ext.submittedAt
      ? formatExtensionSubmittedAt(ext.submittedAt, 'MM/DD/YYYY h:mm A')
      : '',
    staffName: ext.staffName || ext.staffEmail || '',
    staffPhone: joinPhoneLines(ext.staffNumber, ext.staffEmail),
    reason: ext.reason || '',
    length: formatExtensionEstimatedTime(ext.estimatedTime),
    projectManager: '',
  }))
}

export function buildDigitalVosFormData(input: BuildDigitalVosFormInput): DigitalVosFormViewModel {
  const {appointment, meetingSummary, extensions, osiNotes} = input
  const appt = appointment ?? {}
  const summary = meetingSummary ?? {}

  const checkedInAt = appt.checkedInAt ?? appt.CheckedInAt
  const checkOutAt = appt.checkOutAt ?? appt.CheckOutAt

  // VOS (billing) times as submitted by the interpreter, already clamped to the scheduled
  // window by the API. checkedInAt / checkOutAt stay the raw click times and remain the
  // source for the audit views (Meeting Summary, admin appointment detail).
  const vosStart = summary.startDate ?? summary.StartDate ?? checkedInAt
  const vosEnd = summary.endDate ?? summary.EndDate ?? checkOutAt

  const startTime = appt.startTime ?? appt.StartTime
  const endTime = appt.endTime ?? appt.EndTime

  const customerName = readString(appt, 'customerName', 'CustomerName')
  const requesterName = readString(appt, 'requesterName', 'RequesterName')
  const client =
    customerName && requesterName
      ? `${customerName} - ${requesterName}`
      : customerName || requesterName

  const travelH = summary.travalTimeH ?? summary.TravalTimeH ?? summary.interpreterTravelTimeH
  const travelM = summary.travalTimeM ?? summary.TravalTimeM ?? summary.interpreterTravelTimeM

  const extensionBlocks = mapExtensions(extensions)
  const commType = readString(appt, 'communicationTypeIdentification', 'CommunicationTypeIdentification')

  return {
    assignmentNumber: readString(appt, 'code', 'Code'),
    dateAndTime: formatAppointmentDateTime(startTime, appt),
    interpreter: readString(appt, 'confirmedInterpreterName', 'ConfirmedInterpreterName'),
    client,
    language: readString(appt, 'languageTo', 'LanguageTo'),
    estimatedDuration: formatScheduledDuration(startTime, endTime, appt),
    address: readString(appt, 'address', 'Address'),
    instructions: readString(appt, 'description', 'Description'),
    consumer: readString(appt, 'consumer', 'Consumer'),
    signInDate: formatAppointmentDate(checkedInAt, appt),
    arrivalTime: formatAppointmentTime(vosStart, appt),
    signInStaffName: readString(appt, 'checkInStaffName', 'CheckInStaffName'),
    signInPhone: joinPhoneLines(
      readString(appt, 'checkInStaffNumber', 'CheckInStaffNumber'),
      readString(appt, 'checkInStaffEmail', 'CheckInStaffEmail')
    ),
    signInComments: '',
    signOutDate: formatAppointmentDate(checkOutAt, appt),
    departureTime: formatAppointmentTime(vosEnd, appt),
    signOutStaffName: readString(summary, 'staffMemberName', 'StaffMemberName'),
    signOutPhone: joinPhoneLines(
      readString(summary, 'staffMemberPhone', 'StaffMemberPhone'),
      readString(summary, 'staffMemberEmail', 'StaffMemberEmail')
    ),
    signOutComments: readString(summary, 'disputeReason', 'DisputeReason'),
    interpreterSignatureDate: formatAppointmentDate(
      summary.insertedDateTime ?? summary.InsertedDateTime ?? summary.lastModifiedDateTime,
      appt
    ),
    startMileage: '',
    endMileage: '',
    totalMileage:
      summary.milage != null || summary.Milage != null
        ? String(summary.milage ?? summary.Milage)
        : '',
    startTravelTime: '',
    endTravelTime: '',
    totalTravelTime: formatTravelTime(travelH, travelM),
    travelStaffName: readString(summary, 'staffMemberName', 'StaffMemberName'),
    assignmentId: readString(appt, 'code', 'Code'),
    extensions: extensionBlocks,
    hasExtensions: extensionBlocks.length > 0,
    interpreterComments: readNotesText(osiNotes),
    interpretationStartTime: formatAppointmentTime(vosStart, appt),
    interpretationEndTime: formatAppointmentTime(vosEnd, appt),
    breakTimeMinutes: (() => {
      const value =
        readOsiBreakTimeMinutes(summary) ?? readOsiBreakTimeMinutes(appt)
      return value != null ? String(value) : ''
    })(),
    issueNotes: readString(summary, 'disputeReason', 'DisputeReason'),
    showPage3: commType === 'THIRD_PART_PLATFORM',
  }
}

/** OSI meeting summary status: 2 = Approved */
export function isDigitalVosApproved(meetingSummary: Record<string, unknown> | null | undefined): boolean {
  return Number(meetingSummary?.vosSubmissionMode ?? meetingSummary?.VosSubmissionMode) === 1 &&
    Number(meetingSummary?.status ?? meetingSummary?.Status) === 2
}
